import { useEffect, useRef } from 'react';
import { locateFaceMeshAsset } from './mediapipeAssets';

/**
 * FaceProcessor Component
 * Uses MediaPipe FaceMesh to detect and process face landmarks
 */
const FaceProcessor = ({
  videoElement,
  onLandmarksDetected,
  enabled = true,
  quickStart = false,
}) => {
  const faceMeshRef = useRef(null);
  const animationFrameRef = useRef(null);
  const consoleRestoreRef = useRef(null);
  const onLandmarksDetectedRef = useRef(onLandmarksDetected);

  // Keep ref updated with latest callback
  useEffect(() => {
    onLandmarksDetectedRef.current = onLandmarksDetected;
  }, [onLandmarksDetected]);

  useEffect(() => {
    if (!enabled || !videoElement) {
      // Clean up if disabled
      if (faceMeshRef.current) {
        try {
          const faceMesh = faceMeshRef.current;
          faceMeshRef.current = null;
          setTimeout(() => {
            try {
              if (faceMesh && typeof faceMesh.close === 'function') {
                faceMesh.close();
              }
            } catch (e) {
              // Ignore cleanup errors
            }
          }, 100);
        } catch (e) {
          faceMeshRef.current = null;
        }
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    let isMounted = true;
    let isInitializing = false;

    const initializeMediaPipe = async () => {
      // Prevent multiple simultaneous initializations
      if (isInitializing || faceMeshRef.current) {
        return;
      }

      isInitializing = true;

      // Suppress MediaPipe internal errors (Calculator not found, OpenGL warnings)
      // These are benign errors that occur during GPU initialization and don't affect functionality
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      let errorSuppressionActive = false;

      const suppressMediaPipeErrors = () => {
        if (errorSuppressionActive) return;
        errorSuppressionActive = true;

        console.error = (...args) => {
          const message = args[0]?.toString() || '';
          // Suppress MediaPipe calculator errors and WASM errors
          if (
            (message.includes('Calculator') && message.includes('not found')) ||
            message.includes('solutions_wasm.embind') ||
            message.includes('face_mesh_solution')
          ) {
            return; // Suppress these errors
          }
          originalConsoleError.apply(console, args);
        };

        console.warn = (...args) => {
          const message = args[0]?.toString() || '';
          // Suppress MediaPipe Face Mesh WASM / WebGL / pipeline logs so they don't appear in console
          if (
            message.includes('OpenGL error checking is disabled') ||
            message.includes('gl_context.cc') ||
            message.includes('gl_context_webgl') ||
            message.includes('Successfully created a WebGL context') ||
            message.includes('Successfully destroyed WebGL context') ||
            message.includes('ConstantSidePacket') ||
            message.includes('RefineLandmarks') ||
            message.includes('EnableFaceGeometry') ||
            message.includes('facegeometryfromlandmarksandimage') ||
            message.includes('FaceGeometryPipeline') ||
            message.includes('FaceGeometryEnvGenerator') ||
            message.includes('ImagePropertiesCalculator') ||
            message.trim() === '--------------------------' ||
            /^I\d{4}\s+\d{2}:\d{2}:\d{2}/.test(message)
          ) {
            return;
          }
          originalConsoleWarn.apply(console, args);
        };
      };

      const restoreConsole = () => {
        if (errorSuppressionActive) {
          console.error = originalConsoleError;
          console.warn = originalConsoleWarn;
          errorSuppressionActive = false;
        }
      };

      try {
        // Wait a bit to ensure any previous instance is fully cleaned up
        await new Promise((resolve) => setTimeout(resolve, quickStart ? 0 : 100));

        if (!isMounted) {
          restoreConsole();
          isInitializing = false;
          return;
        }

        // Activate error suppression before MediaPipe initialization
        suppressMediaPipeErrors();

        // Dynamic import of MediaPipe FaceMesh
        const { FaceMesh } = await import('@mediapipe/face_mesh');

        if (!isMounted) {
          restoreConsole();
          isInitializing = false;
          return;
        }

        // MediaPipe FaceMesh uses GPU automatically via WebGL when available
        const faceMesh = new FaceMesh({
          locateFile: locateFaceMeshAsset,
        });

        // Store restore function for cleanup
        consoleRestoreRef.current = restoreConsole;

        // Restore console after initialization (MediaPipe errors occur during init)
        setTimeout(() => {
          restoreConsole();
          consoleRestoreRef.current = null;
        }, 2000); // Give MediaPipe time to initialize

        if (!isMounted) {
          restoreConsole();
          try {
            faceMesh.close();
          } catch (e) {
            // Ignore
          }
          isInitializing = false;
          return;
        }

        faceMesh.setOptions({
          maxNumFaces: 2,
          refineLandmarks: true,
          minDetectionConfidence: 0.3,
          minTrackingConfidence: 0.3,
        });

        faceMesh.onResults((results) => {
          if (!isMounted || !videoElement) return;

          const faces = results.multiFaceLandmarks || [];
          if (faces.length > 1) {
            // Multiple faces ΓÇö block capture; still pass primary landmarks for HUD
            if (onLandmarksDetectedRef.current) {
              onLandmarksDetectedRef.current(faces[0], {
                width: videoElement.videoWidth || 640,
                height: videoElement.videoHeight || 480,
                multipleFaces: true,
                faceCount: faces.length,
              });
            }
          } else if (faces.length === 1) {
            if (onLandmarksDetectedRef.current) {
              onLandmarksDetectedRef.current(faces[0], {
                width: videoElement.videoWidth || 640,
                height: videoElement.videoHeight || 480,
                multipleFaces: false,
                faceCount: 1,
              });
            }
          } else if (onLandmarksDetectedRef.current) {
            onLandmarksDetectedRef.current(null, {
              width: videoElement.videoWidth || 640,
              height: videoElement.videoHeight || 480,
              multipleFaces: false,
              faceCount: 0,
            });
          }
        });

        faceMeshRef.current = faceMesh;
        isInitializing = false;

        // Process video frames with throttling for stabilization (target ~25fps)
        let lastProcessTime = 0;
        const processFrame = async () => {
          if (!isMounted || !videoElement || !faceMeshRef.current || videoElement.readyState < 2) {
            if (isMounted && faceMeshRef.current) {
              animationFrameRef.current = requestAnimationFrame(processFrame);
            }
            return;
          }

          const now = Date.now();
          const frameIntervalMs = quickStart ? 28 : 40;
          if (now - lastProcessTime >= frameIntervalMs) {
            try {
              lastProcessTime = now;
              await faceMeshRef.current.send({ image: videoElement });
            } catch (err) {
              // Error handled silently
            }
          }

          if (isMounted && faceMeshRef.current) {
            animationFrameRef.current = requestAnimationFrame(processFrame);
          }
        };

        // Wait for video to be ready before starting processing
        const checkVideoReady = () => {
          if (!isMounted || !videoElement || !faceMeshRef.current) {
            return;
          }
          if (videoElement.readyState >= 2) {
            processFrame();
          } else {
            setTimeout(checkVideoReady, quickStart ? 16 : 100);
          }
        };

        checkVideoReady();
      } catch (err) {
        // Restore console in case of error
        restoreConsole();
        isInitializing = false;
      }
    };

    // Small delay before initialization to ensure cleanup is complete
    const initTimeout = setTimeout(initializeMediaPipe, quickStart ? 0 : 150);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);

      // Restore console if it was overridden
      if (consoleRestoreRef.current) {
        consoleRestoreRef.current();
        consoleRestoreRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (faceMeshRef.current) {
        try {
          // Wait a bit for any pending operations to complete
          const faceMesh = faceMeshRef.current;
          faceMeshRef.current = null;
          // Close asynchronously to avoid blocking
          setTimeout(() => {
            try {
              if (faceMesh && typeof faceMesh.close === 'function') {
                faceMesh.close();
              }
            } catch (e) {
              // Ignore cleanup errors - MediaPipe might already be closed
            }
          }, 200);
        } catch (e) {
          // Ignore cleanup errors
          faceMeshRef.current = null;
        }
      }
    };
  }, [enabled, videoElement, quickStart]);

  return null; // This component doesn't render anything
};

export default FaceProcessor;

